import * as React from "react";
import * as S from "@styled";
import { connect } from "react-redux";
import SplitPane from "react-split-pane";
import { selectElement } from "../actions/SelectElement";
import { ElementType, IElement, } from "../model/IProcessData";
import { Process } from "../model/Process";
import { IDocumentationState, IPanelSettings, PanelType, ProcessSidePanelType, State } from "../model/State";
import { ValidationErrorDictionary } from "../model/ValidationData";
import { default as EditorPanel } from "./EditorPanel";
import { default as SaveProcessForm } from "./SaveProcessForm";
import SidePanelContent, { ParameterEditorContainerId, TestProcessParameterEditorContainerId } from "./SidePanel";

import { createSelector } from "reselect";
import { DispatchType } from "../../shared/utilities";
import { minimapButtonClicked } from "../actions/minimapButtonClickedAction";
import { addOrSelectTestPanel, closeDocumentationPanel, closeElementPanel, closeProcessSidePanel, openProcessSidePanel, pinProcessSidePanel, TestElementId, toggleDocumentationPanel, updatePanelWidth } from "../actions/SidePanelActions";
import { getElementDocumentationIdentifier, getElementName } from "./Helpers";
import { default as ParameterEditor } from "./ParameterEditor";
import { SidePanelControls } from "./SidePanelControls";
import { default as SettingsEditor } from "./SettingsEditor";
import { default as TestProcessPanel } from "./TestProcessPanel";
import { default as SnippetsPanel } from "./SnippetsPanel";
import { PageLoadSpinner } from "shared/components/PageLoadSpinner";
import ProcessVariablesPanel from "./ProcessVariablesPanel";
import DocumentationPanel from "./DocumentationPanel";
import styled from "styled-components";
import { linkColor } from "shared/styles/theme";
import { saveToLocalStorage } from "processEditor/actions/ProcessToLocalStorageActions";
import BasedOnTemplateWarning from "./BasedOnTemplateWarning";
import { updateDocumentationPanel } from "processEditor/actions/UpdateDocumentationPanelActions";

function disableFormSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
}
// anything smaller than this looked stupid as the control items on the top of the side panel got wider than the actual side panel
export const sidePanelMinSize = 330;
const StyledDocumentationPanel = styled.div`
    padding: 0 24px;
    min-width: 0;
    img {
        max-width: 100%;
    }
    a.intercom-content-link {
        color: ${linkColor}
    }
`;

export const ProcessEditor: React.FC<ProcessEditorProps> = (props: ProcessEditorProps) => {

    React.useEffect(() => {
        if ((props.process?.id || props.process?.uniqueIdentifier) && props.hasChanges) {
            props.onProcessHasChanges();
        }
    }, [props, props.hasChanges]);

    React.useEffect(() => {
        const selectedDocumentationId = props?.selectedElementTypeDocumentationIdentifier;
        if (selectedDocumentationId && selectedDocumentationId !== "Start") {
            // We'll skip triggers here as they are handled in the TriggerEditor
            props?.updateDocumentation({ identifier: selectedDocumentationId, type: "element" });
        }
    }, [props, props.selectedElementTypeDocumentationIdentifier]);

    const handleGlobalErrorHandleSelect = () => {
        handleSelectElement({ id: "globalErrorHandler", type: ElementType.GlobalErrorHandler, parameters: {} });
    };

    const handleSelectElement = (element: IElement) => {
        if (element == null || element.id !== props?.panelSettings?.elementPanel.selectedElementId) {
            props?.selectElement(element);
        }
    };
    const handleSelectSettingsPanel = () => {
        props?.selectSettingsPanel();
    };
    const handleSelectVariablesPanel = () => {
        props?.selectVariablesPanel();
    };
    const handleSelectTestPanel = () => {
        props?.selectTestPanel();
    };
    const handleCloseSidePanel = () => {
        props?.closeSidePanel();
    };
    const handleMinimapButtonClicked = () => {
        minimapButtonClicked();
    };
    const handleSnippetsButtonClicked = () => {
        props?.selectSnippetsPanel();
    }

    const getPanelTitle = (selectedPanelType: ProcessSidePanelType) => {
        switch (selectedPanelType) {
            case ProcessSidePanelType.SettingsPanel: {
                return FRENDS.Localizations.Settings;
            }
            case ProcessSidePanelType.VariablesPanel: {
                return "Variables"
            }
            case ProcessSidePanelType.TestPanel: {
                return FRENDS.Localizations.Test;
            }
            case ProcessSidePanelType.SnippetsPanel: {
                return FRENDS.Localizations.Snippets;
            }
        }
    }

    const getPanelToggleClickHandlerForDocumentationButton = (selectedPanelType: ProcessSidePanelType) => {
        switch (selectedPanelType) {
            case ProcessSidePanelType.SnippetsPanel: {
                return () => window.open('https://docs.frends.com/en/articles/6006971-code-snippets', '_blank');
            }
            default: {
                return undefined;
            }
        }
    }

    const getPanel = (selectedPanelType: ProcessSidePanelType) => {
        switch (selectedPanelType) {
            case ProcessSidePanelType.SettingsPanel: {
                return <SettingsEditor
                    disableEdit={props?.disableEdit || props?.isSaving}
                    validationErrors={props?.validation["errorcases"]}
                    onProcessErrorHandlerShowClick={handleGlobalErrorHandleSelect}
                    process={props?.process}
                    processGlobalErrorHandlerName={props?.processGlobalErrorHandlerName}
                />
            }
            case ProcessSidePanelType.VariablesPanel: {
                return <ProcessVariablesPanel
                    disableEdit={props?.disableEdit || props?.isSaving}
                />
            }
            case ProcessSidePanelType.TestPanel: {
                return <TestProcessPanel selectedElementId={TestElementId} />
            }
            case ProcessSidePanelType.SnippetsPanel: {
                return <SnippetsPanel />
            }
        }
    }
    const genericSidePanelVisible = props?.panelSettings?.processSidePanel.isPinned || props?.panelSettings?.processSidePanel.isOpen;
    const documentationSidePanelVisible = props.isDocumentationPanelOpen || props.selectedElementTypeName === "Data Object reference" || props.selectedElementTypeName === "Data Store reference";
    const elementEditorSidePanelVisible = props?.panelSettings?.elementPanel.isOpen && props.panelSettings.elementPanel.selectedElementId && (props.selectedElementTypeName !== "Data Object reference" && props.selectedElementTypeName !== "Data Store reference")

    const updatePanelWidth = (panelType: PanelType, newSize: number) => {
        props.updatePanelWidth(panelType, newSize);
    }

    return <div>
        {props?.isSaving ? <PageLoadSpinner /> : null}
        <form autoComplete="off" className="form-field" onSubmit={disableFormSubmit}>
            <S.ProcessEditorCanvas>
                {props.process.processTemplate && (
                    <BasedOnTemplateWarning deviated={!!props.process.processTemplateDeviated} />
                )}
                {/* Documentation panel */}
                {/* @ts-expect-error: ACC-8426: react-split-pane has not updated their types to include React.PropsWithChildren which is required by React 18 */}
                <SplitPane
                    resizerStyle={{ display: documentationSidePanelVisible ? "block" : "none" }}
                    split="vertical"
                    primary="second"
                    minSize={sidePanelMinSize}
                    pane2Style={{
                        display: documentationSidePanelVisible
                            ? "flex"
                            : "none"
                    }}
                    onDragFinished={(newSize) => updatePanelWidth(PanelType.DocumentationPanel, newSize)}
                    size={props?.panelSettings?.[PanelType.DocumentationPanel].width}
                >
                    {/* Settings/Variable/Test/Snippets panel */}
                    {/* @ts-expect-error: ACC-8426: react-split-pane has not updated their types to include React.PropsWithChildren which is required by React 18 */}
                    <SplitPane
                        resizerStyle={{ display: genericSidePanelVisible ? "block" : "none" }}
                        split="vertical"
                        primary="second"
                        minSize={sidePanelMinSize}
                        pane2Style={{
                            display: genericSidePanelVisible ? "flex" : "none"
                        }}
                        onDragFinished={(newSize) => updatePanelWidth(PanelType.ProcessSidePanel, newSize)}
                        size={props?.panelSettings?.[PanelType.ProcessSidePanel].width}
                    >
                        {/* Parameter editor panel */}
                        {/* @ts-expect-error: ACC-8426: react-split-pane has not updated their types to include React.PropsWithChildren which is required by React 18 */}
                        <SplitPane
                            resizerStyle={{ display: elementEditorSidePanelVisible ? "block" : "none" }}
                            split="vertical"
                            primary="second"
                            minSize={sidePanelMinSize}
                            pane2Style={{
                                display: elementEditorSidePanelVisible
                                    ? "flex"
                                    : "none"
                            }}
                            onDragFinished={(newSize) => updatePanelWidth(PanelType.ElementPanel, newSize)}
                            size={props?.panelSettings?.[PanelType.ElementPanel].width}
                        >
                            <EditorPanel
                                {...props}
                                handleGlobalErrorHandleSelect={handleGlobalErrorHandleSelect}
                                handleSelectElement={handleSelectElement}
                                selectSettingsPanel={props?.selectSettingsPanel}
                            />
                            {elementEditorSidePanelVisible ?
                                <SidePanelContent onCloseButton={props?.closeElementPanel} title={props.selectedElementTypeName} containerId={ParameterEditorContainerId} toggleClickDocumentationButton={props.isIntercomDocumentationEnabled ? props.handleToggleDocumentationPanel : undefined}>
                                    <ParameterEditor key="ElementEditor" id={props.panelSettings.elementPanel.selectedElementId!} />
                                </SidePanelContent> : <span></span>
                            }
                        </SplitPane>
                        {genericSidePanelVisible ?
                            <SidePanelContent
                                containerId={TestProcessParameterEditorContainerId}
                                isPanelPinned={props?.panelSettings?.processSidePanel.isPinned}
                                onPinButton={props?.pinSidePanel}
                                onCloseButton={handleCloseSidePanel}
                                title={getPanelTitle(props.panelSettings.processSidePanel.selectedPanelType)}
                                toggleClickDocumentationButton={getPanelToggleClickHandlerForDocumentationButton(props.panelSettings.processSidePanel.selectedPanelType)}
                            >
                                {getPanel(props.panelSettings.processSidePanel.selectedPanelType)}
                            </SidePanelContent> : <span></span>
                        }
                    </SplitPane>
                    {documentationSidePanelVisible ?
                        <SidePanelContent onCloseButton={props.handleCloseDocumentationPanel} title={"Documentation"} containerId={"process-editor-documentation-container"}>
                            {props.isIntercomDocumentationEnabled && (
                                <StyledDocumentationPanel>
                                    <DocumentationPanel
                                        title={props.selectedElementTypeName}
                                    />
                                </StyledDocumentationPanel>
                            )}
                        </SidePanelContent> : <span></span>
                    }
                </SplitPane>
            </S.ProcessEditorCanvas>
        </form>
        <SidePanelControls
            onSelectTestPanel={handleSelectTestPanel}
            onSelectVariablesPanel={handleSelectVariablesPanel}
            onSelectSettingsPanel={handleSelectSettingsPanel}
            onClickMinimapButton={handleMinimapButtonClicked}
            onClickSnippetsButton={handleSnippetsButtonClicked}
            activePaneltype={(props?.panelSettings?.processSidePanel.isPinned || props?.panelSettings?.processSidePanel.isOpen) && props.panelSettings.processSidePanel.selectedPanelType}
        />
        <SaveProcessForm />
    </div >;
};

interface ProcessEditorOwnProps {
}

export interface ProcessEditorStateProps {
    disableEdit: boolean | undefined;
    isSaving: boolean | undefined;
    hasChanges: boolean | undefined;
    validation: ValidationErrorDictionary;
    panelSettings: IPanelSettings;
    selectedElementTypeName: string | undefined | null;
    process: Process;
    processGlobalErrorHandlerName: string | undefined;
    isIntercomDocumentationEnabled: boolean | undefined;
    isDocumentationPanelOpen: boolean;
    selectedElementTypeDocumentationIdentifier: string | undefined | null;
}

interface ProcessEditorDispatchProps {
    selectElement: (element: IElement) => void;
    updateDocumentation: (documentation: IDocumentationState) => void;
    handleSelectPanel: () => void;
    selectSettingsPanel: () => void;
    selectVariablesPanel: () => void;
    selectTestPanel: () => void;
    selectSnippetsPanel: () => void;
    closeSidePanel: () => void;
    closeElementPanel: () => void;
    pinSidePanel: () => void;
    handleToggleDocumentationPanel: () => void;
    handleCloseDocumentationPanel: () => void;
    onProcessHasChanges: () => void;
    updatePanelWidth: (panelType: PanelType, width: number) => void;
}

export type ProcessEditorProps = ProcessEditorOwnProps & ProcessEditorStateProps & ProcessEditorDispatchProps;

const mapStateToProps = (state: State): ProcessEditorStateProps => {
    let errorHandlersubProcessName: string | undefined = "";
    if (state.process.hasGlobalErrorHandler && state.elements["globalErrorHandler"] && state.elements["globalErrorHandler"].selectedTypeId) {
        errorHandlersubProcessName = state.subProcesses.find((x) => x.uniqueIdentifier === state.elements["globalErrorHandler"].selectedTypeId)?.name;
    }
    return {
        validation: state.validation.validationDictionary,
        disableEdit: state.uiState.disableEdit,
        isSaving: state.uiState.isSaving,
        hasChanges: state.uiState.hasChanges,
        panelSettings: state.uiState.panelSettings,
        selectedElementTypeName: getElementTypeName(state),
        process: state.process,
        processGlobalErrorHandlerName: errorHandlersubProcessName,
        isIntercomDocumentationEnabled: state.uiState.isIntercomDocumentationEnabled,
        isDocumentationPanelOpen: state.uiState.panelSettings.documentationPanel.isOpen,
        selectedElementTypeDocumentationIdentifier: getElementTypeDocumentationIdentifier(state)
    };
};

const getElements = (state: State) => state.uiState.panelSettings?.elementPanel.selectedElementId ?
    state.elements[state.uiState.panelSettings.elementPanel.selectedElementId] : null;

const getElementTypeName = createSelector(
    [getElements],
    (element) => {
        return element ? getElementName(element.type) : null;
    }
);

const getElementTypeDocumentationIdentifier = createSelector(
    [getElements],
    (element) => {
        return element ? getElementDocumentationIdentifier(element) : null;
    }
)

const mapDispatchToProps = (dispatch: DispatchType<State>): ProcessEditorDispatchProps => {
    return {
        selectSettingsPanel: () => dispatch(openProcessSidePanel(ProcessSidePanelType.SettingsPanel)),
        selectVariablesPanel: () => dispatch(openProcessSidePanel(ProcessSidePanelType.VariablesPanel)),
        selectSnippetsPanel: () => dispatch(openProcessSidePanel(ProcessSidePanelType.SnippetsPanel)),
        selectTestPanel: () => dispatch(addOrSelectTestPanel()),
        selectElement: (element: IElement) => dispatch(selectElement(element)),
        updateDocumentation: (documentation: IDocumentationState) => dispatch(updateDocumentationPanel(documentation)),
        closeSidePanel: () => dispatch(closeProcessSidePanel()),
        pinSidePanel: () => dispatch(pinProcessSidePanel()),
        closeElementPanel: () => dispatch(closeElementPanel()),
        handleSelectPanel: () => null,
        handleCloseDocumentationPanel: () => dispatch(closeDocumentationPanel()),
        handleToggleDocumentationPanel: () => dispatch(toggleDocumentationPanel()),
        onProcessHasChanges: () => dispatch(saveToLocalStorage()),
        updatePanelWidth: (panelType: PanelType, width: number) => dispatch(updatePanelWidth(panelType, width))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProcessEditor);
